import { useQuery } from '@tanstack/react-query';

import { useAuthenticationStore } from '$/components/core/Authentication/stores/useAuthenticationStore';
import {
  userRoleDefinition,
  type UserRole,
} from '$/services/usecases/authentication/mapper/jwt';
import { projectsQuery } from '$/services/usecases/projects/queries';

const loggedInRoles: UserRole[] = [
  'pro',
  'keyUser', //'pro + keyUser für neue Features',
  'countryAdmin',
  'superAdmin',
  'student',
  'teacher',
  'superTeacher',
] satisfies UserRole[];
const adminRoles: UserRole[] = ['countryAdmin', 'superAdmin'];
const teacherRoles: UserRole[] = ['teacher', 'superTeacher'];

const permissionMatrix = {
  General_Login_Register: ['free'],
  General_User_Profile: [...loggedInRoles],
  General_Project_Page: [...loggedInRoles],
  General_Abo_Page: ['free'],
  General_Welcome_Project_Count: ['community', 'standard'],
  Favorites_Open: [...loggedInRoles],
  Favorites_Select: [...loggedInRoles],
  Project_Create: [...loggedInRoles],
  Project_Open: [...loggedInRoles],
  Project_Share: [...loggedInRoles],
  Project_Save: [...loggedInRoles],
  Project_SchoolClassFolder: ['student'],
  DesignGuide_Open: [...loggedInRoles],
  DesignGuide_See: ['superAdmin', 'keyUser'],
  Editor_Use_Texture_Categories: [...loggedInRoles],
  View_All_Collections: [...loggedInRoles],
  Use_Pro_Materials: [...loggedInRoles],
  Admin_Access: [...adminRoles],
  Admin_User_Management: ['superAdmin'],
  Admin_Key_User_Management: ['superAdmin'],
  Admin_Country_Management: ['superAdmin'],
  Admin_Producer_Management: ['countryAdmin'],
  Admin_Contact_Management: ['countryAdmin'],
  Admin_AI_Prompts_Management: ['superAdmin'],
  School_See_Profile: ['superTeacher'],
  School_Manage_Profile: ['superTeacher'],
  School_Manage_Teachers: ['superTeacher'],
  School_Create_Class: [...teacherRoles],
  School_Manage_Class: [...teacherRoles],
  School_See_Classes: [...teacherRoles],
  School_See_Students: [...teacherRoles],
  School_Manage_Students: [...teacherRoles],
  School_Share_Projects_With_Class: [...teacherRoles],
  School_Save_Projects_In_Class: ['student'],
  Manage_School_Requests: ['superAdmin'],
  AI_Workflows_Execute: ['superAdmin', 'keyUser'],
};

export type Permission = keyof typeof permissionMatrix;

export const hasPermissionFactory =
  (userRole: UserRole = 'free') =>
  (requestedPermission: Permission) => {
    const allowedRoles = permissionMatrix[requestedPermission] ?? [];
    return allowedRoles.includes(userRole);
  };

const getProjectCountLimit =
  (userRole: UserRole = 'free') =>
  () => {
    return userRoleDefinition[userRole].projectLimit;
  };

const canCreateMoreProjects =
  (userRole: UserRole = 'free', userProjectCount: number) =>
  () => {
    return getProjectCountLimit(userRole)() > userProjectCount;
  };

export const useAuthorization = () => {
  const user = useAuthenticationStore.useUser();
  const { data } = useQuery(projectsQuery({ enabled: user != null }));

  const projectCount = data?.response.payload.length ?? 0;

  return {
    hasPermission: hasPermissionFactory(user?.role),
    canCreateMoreProjects: canCreateMoreProjects(user?.role, projectCount),
    getProjectCountLimit: getProjectCountLimit(user?.role),
  };
};
